'use client';

import { FC, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useTranslations } from 'next-intl';
import {
  Card,
  CardCollapseBody,
  CardSubtitle,
  CardTitle,
  CardTitleHeader,
  CollapseToggle,
} from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/styled';
import { WlasChevronIcon } from '../WlasParamsCard/components/WlasSection/WlasChevronIcon';
import { WidgetPreviewWrapper } from './styled';

export interface WlasWidgetPreviewHandle {
  scrollIntoView: () => void;
}

interface WlasWidgetPreviewProps {
  attrs: Record<string, string>;
}

export const WlasWidgetPreview: FC<
  WlasWidgetPreviewProps & { ref?: React.Ref<WlasWidgetPreviewHandle> }
> = forwardRef<WlasWidgetPreviewHandle, WlasWidgetPreviewProps>(({ attrs }, ref) => {
  const t = useTranslations('wlasWidget');
  const [open, setOpen] = useLocalStorage('wlas-card-preview-open', true);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    scrollIntoView: () => {
      setOpen(true);
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    },
  }));

  const attrsRef = useRef(attrs);
  attrsRef.current = attrs;

  useEffect(() => {
    if (!open) return;
    if (Object.keys(attrsRef.current).length === 0) return;
    const container = containerRef.current;
    if (!container) return;

    const old = container.querySelector('alleimmobilien-wlas-widget');
    if (old) old.remove();

    const el = document.createElement('alleimmobilien-wlas-widget');
    for (const [k, v] of Object.entries(attrsRef.current)) {
      el.setAttribute(k, v);
    }
    container.appendChild(el);
  }, [open, attrs]);

  return (
    <Card ref={cardRef}>
      <CardTitleHeader onClick={() => setOpen((v) => !v)}>
        <div>
          <CardTitle>{t('preview.label')}</CardTitle>
          <CardSubtitle>{t('preview.subtitle')}</CardSubtitle>
        </div>
        <CollapseToggle
          type="button"
          aria-expanded={open}
          aria-controls="wlas-preview-body"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          <WlasChevronIcon open={open} />
        </CollapseToggle>
      </CardTitleHeader>
      {open && (
        <CardCollapseBody>
          <WidgetPreviewWrapper id="wlas-preview-body">
            <div ref={containerRef} />
          </WidgetPreviewWrapper>
        </CardCollapseBody>
      )}
    </Card>
  );
});
WlasWidgetPreview.displayName = 'WlasWidgetPreview';
